import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ShoppingBag, Sparkles, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { trackEvent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const stripHtml = (s) => (s || "").replace(/<[^>]*>/g, "").trim();

const ProductCard = ({ product, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.06, duration: 0.4 }}
    className="group relative bg-brand-surface border border-brand-border hover:border-brand-gold/60 transition-colors flex flex-col"
    data-testid={`shop-product-${product.id}`}
  >
    {product.is_new && (
      <span data-testid={`just-in-badge-${product.id}`} className="absolute top-3 left-3 z-10 flex items-center gap-1 bg-brand-gold text-black font-sans text-[10px] font-bold uppercase tracking-wider px-2.5 py-1">
        <Sparkles size={11} /> Just In
      </span>
    )}
    <div className="aspect-square overflow-hidden bg-brand-bg">
      {product.image_url ? (
        <img src={product.image_url} alt={product.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-brand-text-muted"><ShoppingBag size={40} /></div>
      )}
    </div>
    <div className="p-4 flex flex-col flex-1">
      <h3 className="font-serif text-base text-brand-text mb-1">{product.name}</h3>
      {stripHtml(product.short_description) && (
        <p className="font-sans text-xs text-brand-text-muted line-clamp-2 mb-2">{stripHtml(product.short_description)}</p>
      )}
      <div className="mt-auto flex items-center justify-between pt-2">
        <span className="font-sans text-lg text-brand-gold font-semibold">{product.price ? `$${product.price}` : ""}</span>
        <span className={`font-sans text-[10px] uppercase ${product.stock_status === "instock" ? "text-green-400" : "text-red-400"}`}>
          {product.stock_status === "instock" ? "In stock" : product.stock_status ? "Out of stock" : ""}
        </span>
      </div>
      {product.permalink && (
        <a
          data-testid={`shop-buy-${product.id}`}
          href={product.permalink}
          target="_blank"
          rel="noreferrer"
          onClick={() => trackEvent("select_item", { item_id: product.id, item_name: product.name, item_category: product.category, price: product.price })}
          className="mt-3 flex items-center justify-center gap-2 w-full py-2.5 bg-brand-gold text-black font-sans text-xs font-semibold hover:bg-brand-gold-light transition-colors"
        >
          Shop Now <ExternalLink size={12} />
        </a>
      )}
    </div>
  </motion.div>
);

export default function ShopPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    document.title = "Shop Hair & Beauty | Afro Magic Braiding Hair";
    axios.get(`${API}/shop/products`)
      .then((r) => {
        setProducts(r.data);
        if (r.data.length) trackEvent("view_item_list", { item_list_name: "Shop", items_count: r.data.length });
      })
      .catch((e) => console.error("Failed to load shop products:", e))
      .finally(() => setLoading(false));
    return () => { document.title = "Afro Magic Braiding Hair"; };
  }, []);

  const FILTER_ORDER = ["Oils", "Gels", "Tools", "Hair Care"];
  const present = FILTER_ORDER.filter((c) => products.some((p) => p.category === c));
  const filters = present.length > 1 ? ["All", ...present] : [];
  const visible = filter === "All" ? products : products.filter((p) => p.category === filter);

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="shop-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <Link to="/" data-testid="shop-back-link" className="inline-flex items-center gap-2 font-sans text-xs text-brand-text-muted hover:text-brand-gold transition-colors mb-8">
          <ArrowLeft size={14} /> Back to Home
        </Link>
        <div className="mb-10">
          <p className="font-sans text-xs uppercase tracking-[0.3em] text-brand-gold mb-3">Curated by Afro Magic</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-brand-text">Shop Hair &amp; Beauty</h1>
          <p className="font-sans text-sm text-brand-text-muted mt-3 max-w-xl">
            Hand-picked hair care and beauty products we trust for your braids and natural hair.
          </p>
        </div>

        {filters.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8" data-testid="shop-filter-bar">
            {filters.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                data-testid={`shop-filter-${c.toLowerCase().replace(" ", "-")}`}
                className={`font-sans text-xs px-4 py-2 border transition-colors ${filter === c ? "border-brand-gold text-black bg-brand-gold font-semibold" : "border-brand-border text-brand-text-muted hover:border-brand-gold hover:text-brand-gold"}`}
              >
                {c}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <p className="font-sans text-sm text-brand-text-muted py-16 text-center">Loading products…</p>
        ) : products.length === 0 ? (
          <div data-testid="shop-empty-state" className="border border-brand-border bg-brand-surface text-center py-20 px-6">
            <ShoppingBag size={36} className="mx-auto text-brand-gold mb-4" />
            <h2 className="font-serif text-lg text-brand-text mb-2">New products coming soon</h2>
            <p className="font-sans text-sm text-brand-text-muted max-w-md mx-auto">
              We're curating a collection of premium hair &amp; beauty products. Check back shortly — or book your next braiding appointment while you wait.
            </p>
            <Link to="/" data-testid="shop-empty-book-btn" className="inline-block mt-6 px-6 py-3 bg-brand-gold text-black font-sans text-xs font-semibold hover:bg-brand-gold-light transition-colors">
              Book an Appointment
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="shop-products-grid">
            {visible.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            {visible.length === 0 && (
              <p data-testid="shop-filter-empty" className="col-span-full font-sans text-sm text-brand-text-muted py-10 text-center">
                No {filter.toLowerCase()} products right now — check the other categories.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
